import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, MapPin, Sparkles, Shield, Users, Train, ChevronLeft, AlertCircle, ExternalLink, BedDouble } from "lucide-react";
import AccommodationCard from "../components/AccommodationCard";
import SafeImage from "../components/SafeImage";
import Footer from "../components/Footer";
import { bookingCityUrl, hostelworldCityUrl, gygCityUrl, trackAffiliateClick } from "../lib/affiliates";
import BookmarkHeart from "../components/BookmarkHeart";

export default function CityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [accs, setAccs] = useState([]);
  const [safety, setSafety] = useState(null);
  const [filter, setFilter] = useState("all"); // all | hostel | hotel | boutique | groups | minors

  useEffect(() => {
    setCity(null);
    setAccs([]);
    setSafety(null);
    Promise.all([
      api.get(`/cities/${slug}`),
      api.get(`/accommodations`, { params: { city: slug } }),
      api.get(`/city-safety/${slug}`).catch(() => ({ data: null })),
    ])
      .then(([c, a, s]) => {
        setCity(c.data);
        setAccs(a.data);
        setSafety(s.data);
      })
      .catch(() => navigate("/map"));
  }, [slug, navigate]);

  if (!city) {
    return <div className="pt-32 text-center text-cream-dim font-display tracking-widest">Loading city…</div>;
  }

  const filtered = accs.filter((a) => {
    if (filter === "all") return true;
    if (filter === "groups") return a.capacity_groups >= 20;
    if (filter === "minors") return a.tags?.includes("minors_supervised") || a.tags?.includes("school_groups");
    if (filter === "solo_female_verified") return a.safety_v2?.badges?.verified_solo_female;
    if (filter === "minors_verified") return a.safety_v2?.badges?.verified_for_minors;
    return a.type === filter;
  });

  return (
    <div data-testid="city-detail-page" className="pt-safe-navbar">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <SafeImage
          src={city.image}
          alt={city.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/30" />
        <div className="relative max-w-[1400px] mx-auto px-6 py-12 w-full">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-cream-dim hover:text-gold transition-colors text-sm mb-6"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <div className="iq-overline mb-3">{city.country}</div>
          <h1 className="font-display text-cream text-6xl md:text-8xl tracking-tight leading-none">{city.name}</h1>
          <p className="font-serif italic text-cream/90 text-xl md:text-2xl mt-3 max-w-2xl">{city.tagline}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              data-testid="city-depart-btn"
              disabled={!city.station_id}
              onClick={() => navigate(`/search?from_id=${encodeURIComponent(city.station_id)}&from_name=${encodeURIComponent(city.station_name)}`)}
              className="iq-btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Depart from {city.name} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="city-arrive-btn"
              disabled={!city.station_id}
              onClick={() => navigate(`/search?to_id=${encodeURIComponent(city.station_id)}&to_name=${encodeURIComponent(city.station_name)}`)}
              className="iq-btn-secondary"
            >
              Arrive at {city.name}
            </button>
            <div className="ml-auto flex items-center gap-2 bg-gold/15 border border-gold/40 px-3 py-2 rounded-sm">
              <Sparkles className="h-4 w-4 text-gold" />
              <span className="font-display text-gold tracking-wider">+{city.xp} XP on visit</span>
            </div>
            <BookmarkHeart kind="city" slug={city.slug} label={city.name} />
          </div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 py-12 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Side rail */}
        <aside className="space-y-6">
          <div className="iq-card p-5">
            <div className="iq-overline mb-3"><Train className="inline h-3 w-3 mr-1" /> Main Station</div>
            <p className="font-display text-cream text-xl tracking-wider">{city.station_name}</p>
            <p className="text-cream-dim text-xs mt-2">Lat {city.lat.toFixed(3)} · Lon {city.lon.toFixed(3)}</p>
          </div>

          <div className="iq-card p-5">
            <div className="iq-overline mb-3"><MapPin className="inline h-3 w-3 mr-1" /> Landmarks</div>
            <ul className="space-y-2">
              {city.landmarks.map((l) => (
                <li key={l} className="text-cream text-sm flex items-start gap-2">
                  <span className="mt-1 h-1 w-1 rounded-full bg-gold shrink-0" /> {l}
                </li>
              ))}
            </ul>
          </div>

          <div className="iq-card p-5">
            <div className="iq-overline mb-3 flex items-center justify-between">
              <span><Shield className="inline h-3 w-3 mr-1" /> Live Safety</span>
              {safety?.data_source === "numbeo_live" && (
                <span className="text-[9px] tracking-widest text-emerald-300/80 bg-emerald-500/10 border border-emerald-400/30 px-1.5 py-0.5 rounded-sm">LIVE</span>
              )}
            </div>
            {safety ? (
              <>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-cream text-4xl tracking-tight">{Math.round(safety.safety_index)}</span>
                  <span className="text-cream-dim/70 text-xs">/ 100 Safety Index</span>
                </div>
                {safety.advisory && (
                  <a
                    href={safety.advisory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-start gap-2 text-xs mb-3 p-2 rounded-sm border ${
                      safety.advisory.color === "green"
                        ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-200"
                        : safety.advisory.color === "amber"
                        ? "bg-amber-500/10 border-amber-400/30 text-amber-200"
                        : "bg-red-500/10 border-red-400/30 text-red-200"
                    } hover:bg-opacity-20`}
                  >
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-semibold">{safety.advisory.label}</div>
                      <div className="opacity-70 text-[10px] mt-0.5">UK FCDO advisory →</div>
                    </div>
                  </a>
                )}
                <p className="text-cream-dim text-xs leading-relaxed mb-3">
                  {safety.neighborhood}
                </p>
                {safety.user_ratings.count > 0 && (
                  <div className="text-[10px] uppercase tracking-widest text-gold/80 mb-3">
                    {safety.user_ratings.count} traveller{safety.user_ratings.count === 1 ? "" : "s"} rated this · avg {safety.user_ratings.avg_safety}/10
                  </div>
                )}
                <div className="border-t border-panel-border pt-2 text-[10px] text-cream-dim/60 space-y-1">
                  <div className="iq-overline text-[9px] mb-1">Data sources</div>
                  {safety.sources.map((s) => (
                    <div key={s.name} className="truncate">
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-gold">· {s.name} ↗</a>
                      ) : (
                        <span>· {s.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-cream-dim text-xs leading-relaxed">
                InterQuest Safety Score blends live Numbeo crime data, UK FCDO travel advisories, accommodation features, and (soon) traveller ratings.
              </p>
            )}
          </div>
        </aside>

        {/* Accommodations */}
        <div>
          <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
            <div>
              <div className="iq-overline mb-2">Stay in {city.name}</div>
              <h2 className="font-display text-cream text-3xl md:text-4xl tracking-wider">Accommodations</h2>
            </div>
            <div className="flex items-center gap-1 bg-ink border border-panel-border rounded-sm p-1 flex-wrap">
              {[
                { id: "all", label: "All" },
                { id: "hostel", label: "Hostels" },
                { id: "hotel", label: "Hotels" },
                { id: "boutique", label: "Boutique" },
                { id: "groups", label: "Groups 20+" },
                { id: "minors", label: "Supervised minors" },
                { id: "solo_female_verified", label: "Solo-female verified" },
                { id: "minors_verified", label: "Verified for minors" },
              ].map((f) => (
                <button
                  key={f.id}
                  data-testid={`acc-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors ${
                    filter === f.id ? "bg-gold text-ink font-display" : "text-cream-dim hover:text-cream"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gold/10 border border-gold/30 px-4 py-2.5 rounded-sm text-xs text-cream-dim mb-4 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-gold mt-0.5 shrink-0" />
            <span>
              <strong className="text-cream">Preview data.</strong> Real-time availability arrives with our Hostelworld &amp; Booking partner-API rollout — properties shown reflect curated, verified listings.
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="iq-card p-12 text-center text-cream-dim font-serif italic">
              No properties match this filter for {city.name}. Try another category.
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {filtered.map((a, i) => (
                <AccommodationCard key={a.id} acc={a} index={i} />
              ))}
            </div>
          )}

          {/* Affiliate live-availability CTA */}
          <div data-testid="city-live-stays" className="iq-card mt-6 p-5 md:p-6 grid md:grid-cols-[1fr_auto_auto] gap-3 items-center bg-ink/60">
            <div className="flex items-start gap-3">
              <BedDouble className="h-5 w-5 text-gold mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="iq-overline mb-1">Want live availability?</div>
                <p className="text-cream-dim text-sm leading-relaxed">
                  Check tonight's prices on our partner networks — full inventory, instant confirmation.
                </p>
              </div>
            </div>
            <a
              data-testid="aff-hostelworld"
              href={hostelworldCityUrl(city.name)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackAffiliateClick("hostelworld", { city: city.name, source: "city_detail" })}
              className="iq-btn-secondary justify-center"
            >
              Hostelworld <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              data-testid="aff-booking"
              href={bookingCityUrl(city.name)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackAffiliateClick("booking", { city: city.name, source: "city_detail" })}
              className="iq-btn-secondary justify-center"
            >
              Booking.com <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              data-testid="aff-gyg"
              href={gygCityUrl(city.name)}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackAffiliateClick("gyg", { city: city.name, source: "city_detail" })}
              className="iq-btn-secondary justify-center"
            >
              Tours via GetYourGuide <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          {/* Group CTA */}
          <div className="iq-card mt-8 p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-4 items-center bg-gradient-to-br from-panel to-ink border-gold/30">
            <div>
              <div className="iq-overline mb-2"><Users className="inline h-3 w-3 mr-1" /> Travelling as a group?</div>
              <h3 className="font-display text-cream text-2xl md:text-3xl tracking-wider mb-1">Schools, teams, and tribes welcome.</h3>
              <p className="text-cream-dim text-sm max-w-2xl">
                We negotiate dorm blocks, supervised minors, accessibility, and packed-lunch logistics for groups of 10–500. One contact, one invoice.
              </p>
            </div>
            <Link to={`/groups?city=${city.slug}`} className="iq-btn-primary" data-testid="city-group-cta">
              Plan a group trip <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
